import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';

// Modal para vincular un evento/tarea a UNA entidad: cliente, expediente o trámite.
// Props:
//   open, onClose
//   value = { cliente_id, expediente_id, tramite_id, cliente_manual }
//   onSelect(vinculo)  → recibe { cliente_id, expediente_id, tramite_id, cliente_manual } (mutuamente excluyentes)
//   tabs — qué pestañas mostrar (default todas). Ej: ['expedientes','tramites']
const TAB_META = { clientes: 'Clientes', expedientes: 'Expedientes', tramites: 'Trámites' };

export default function VinculacionModal({ open, onClose, value = {}, onSelect, tabs = ['clientes', 'expedientes', 'tramites'] }) {
  const [tab, setTab] = useState(tabs[0]);
  const [busqueda, setBusqueda] = useState('');
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [sel, setSel] = useState(null);      // { tipo, id, nombre }
  const [manual, setManual] = useState('');

  useEffect(() => {
    if (!open) return;
    setBusqueda('');
    setManual(value.cliente_manual || '');
    // preseleccionar lo ya vinculado
    if (value.expediente_id && tabs.includes('expedientes')) { setTab('expedientes'); setSel({ tipo: 'expediente', id: value.expediente_id }); }
    else if (value.tramite_id && tabs.includes('tramites')) { setTab('tramites'); setSel({ tipo: 'tramite', id: value.tramite_id }); }
    else if (value.cliente_id && tabs.includes('clientes')) { setTab('clientes'); setSel({ tipo: 'cliente', id: value.cliente_id }); }
    else { setTab(tabs[0]); setSel(null); }
    (async () => {
      const [cli, exp, tra] = await Promise.all([
        supabase.from('clientes').select('id, nombre, apellido, dni').eq('eliminado', false).order('apellido'),
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
        supabase.from('tramites').select('id, nombre').eq('eliminado', false).order('nombre'),
      ]);
      setClientes(cli.data || []);
      setExpedientes(exp.data || []);
      setTramites(tra.data || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const items = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (tab === 'clientes') {
      return clientes
        .map(c => ({ tipo: 'cliente', id: c.id, nombre: `${c.apellido || ''} ${c.nombre || ''}`.trim(), sub: c.dni ? `DNI ${c.dni}` : '' }))
        .filter(c => !q || c.nombre.toLowerCase().includes(q) || c.sub.toLowerCase().includes(q));
    }
    if (tab === 'expedientes') {
      return expedientes
        .map(e => ({ tipo: 'expediente', id: e.id, nombre: e.caratula || 'Sin carátula', sub: e.numero || '' }))
        .filter(e => !q || e.nombre.toLowerCase().includes(q) || e.sub.toLowerCase().includes(q));
    }
    return tramites
      .map(t => ({ tipo: 'tramite', id: t.id, nombre: t.nombre || 'Sin nombre', sub: '' }))
      .filter(t => !q || t.nombre.toLowerCase().includes(q));
  }, [tab, busqueda, clientes, expedientes, tramites]);

  function confirmar() {
    const v = { cliente_id: '', expediente_id: '', tramite_id: '', cliente_manual: '' };
    if (manual.trim() && !sel) { v.cliente_manual = manual.trim(); }
    else if (sel?.tipo === 'cliente') v.cliente_id = sel.id;
    else if (sel?.tipo === 'expediente') v.expediente_id = sel.id;
    else if (sel?.tipo === 'tramite') v.tramite_id = sel.id;
    onSelect(v);
    onClose();
  }

  function quitar() {
    onSelect({ cliente_id: '', expediente_id: '', tramite_id: '', cliente_manual: '' });
    onClose();
  }

  if (!open) return null;

  const labelTab = tab === 'clientes' ? 'cliente' : tab === 'expedientes' ? 'expediente' : 'trámite';

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width: 560, maxWidth: '95vw' }}>
        <div className="modal-header">
          <div className="modal-title">Vincular evento</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border2)' }}>
            {tabs.map(key => (
              <button
                key={key}
                onClick={() => { setTab(key); setBusqueda(''); }}
                style={{
                  flex: 1, padding: '11px 0', border: 'none', cursor: 'pointer',
                  background: tab === key ? 'var(--surface)' : 'var(--surface2)',
                  color: tab === key ? 'var(--blue)' : 'var(--text4)',
                  fontWeight: tab === key ? 600 : 400, fontSize: 13,
                  borderBottom: tab === key ? '2px solid var(--blue)' : '2px solid transparent',
                }}
              >{TAB_META[key]}</button>
            ))}
          </div>
        )}

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', height: 380, overflow: 'hidden', padding: 0 }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)' }}>
            <input
              className="form-input"
              placeholder={`Buscar ${labelTab}...`}
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              autoFocus
              style={{ margin: 0, fontSize: 13 }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>
                {busqueda ? 'Sin resultados' : `No hay ${labelTab}s registrados`}
              </div>
            ) : items.map(it => {
              const activo = sel?.tipo === it.tipo && sel?.id === it.id;
              return (
                <div
                  key={it.id}
                  onClick={() => { setSel(activo ? null : it); setManual(''); }}
                  style={{
                    padding: '10px 14px', cursor: 'pointer',
                    borderBottom: '1px solid var(--border2)',
                    background: activo ? 'var(--blue-light)' : 'transparent',
                    borderLeft: activo ? '3px solid var(--blue)' : '3px solid transparent',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: activo ? 600 : 400, color: 'var(--text)' }}>{it.nombre}</div>
                  {it.sub && <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>{it.sub}</div>}
                </div>
              );
            })}
          </div>

          {/* Nombre sin ficha (solo en tab clientes) */}
          {tab === 'clientes' && (
            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border2)', background: 'var(--surface2)' }}>
              <input
                className="form-input"
                placeholder="…o nombre sin ficha"
                value={manual}
                onChange={e => { setManual(e.target.value); if (e.target.value) setSel(null); }}
                style={{ margin: 0, fontSize: 13 }}
              />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn btn-ghost btn-sm" onClick={quitar}>Quitar vinculación</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={confirmar} disabled={!sel && !manual.trim()}>Vincular</button>
          </div>
        </div>
      </div>
    </div>
  );
}
